import { useIsSuperAdmin } from "@/core/auth/hooks";
import { cn } from "@/lib/utils";
import { useLazyExportTasksQuery } from "@/modules/capture/services/captureApi";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import { Button } from "@/modules/shadcn/ui/button";
import { Checkbox } from "@/modules/shadcn/ui/checkbox";
import { PaginationNumbers } from "@/modules/shadcn/ui/pagination";
import { TaskListItem } from "@/modules/ui";
import {
  AutoAnnotationBatchItemStatusEnum,
  Label,
  Task,
  TaskStatusEnum,
} from "@repo/schema";
import {
  ArrowDownWideNarrow,
  ChartSpline,
  Check,
  CircleAlert,
  Download,
  Loader2,
  Settings2,
  Sparkles,
} from "lucide-react";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DEFAULT_SORT } from "../../hooks/useLabelUrlState";
import { AnalyticsDialog } from "../AnalyticsDialog";
import {
  TaskFilterState,
  TaskSortFilterDialog,
  TaskSortState,
} from "../TaskSortFilterDialog";

export interface DataSourcePanelProps {
  tasks: Task[];
  selectedTaskId: number | null;
  annotationCount: number;
  onSelectTask: (taskId: number) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  filter: TaskFilterState;
  labels: Label[];
  sort: TaskSortState;
  onSortFilterChange: (next: {
    sort: TaskSortState;
    filter: TaskFilterState;
  }) => void;
  metricsAvailable?: boolean;
  /** "Show in dataset" preference — hides task-card metric badges when false. */
  showTaskMetrics?: boolean;

  // ── Batch auto-annotation ──────────────────────────────────────────────────
  /**
   * Images picked for the next batch. Accumulates ACROSS pages, so this can
   * name ids that are not in `tasks` — the footer count is the whole
   * selection, while the header checkbox only ever speaks for this page.
   */
  selectedIds: Set<number>;
  onToggleSelect: (taskId: number, shiftKey: boolean) => void;
  /** Select or clear every image on the current page. */
  onToggleSelectPage: () => void;
  onClearSelection: () => void;
  /** Per-image state of the live batch, keyed by task id. Empty when idle. */
  batchItemStatus: Map<number, AutoAnnotationBatchItemStatusEnum>;
  batchRunning: boolean;
  /**
   * Images with a single-image run in flight from this tab. Shows the Running
   * chip the instant ✨ is clicked; `task.running` (the server's turn lock)
   * carries the same chip across reloads and other tabs.
   */
  runningTaskIds: ReadonlySet<number>;
  onAutoAnnotate: () => void;
  onOpenAutoAnnotateSettings: () => void;
}

export const DataSourcePanel = ({
  tasks,
  selectedTaskId,
  annotationCount,
  onSelectTask,
  page,
  totalPages,
  onPageChange,
  filter,
  labels,
  sort,
  onSortFilterChange,
  metricsAvailable = false,
  showTaskMetrics = true,
  selectedIds,
  onToggleSelect,
  onToggleSelectPage,
  onClearSelection,
  batchItemStatus,
  batchRunning,
  runningTaskIds,
  onAutoAnnotate,
  onOpenAutoAnnotateSettings,
}: DataSourcePanelProps) => {
  const { t } = useTranslation(["label", "common"]);
  const listRef = useRef<HTMLDivElement>(null);
  const [sortFilterDialogOpen, setSortFilterDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [activeProject] = useActiveProject();
  const [triggerExport, { isFetching: isExporting }] =
    useLazyExportTasksQuery();
  // Dataset export is a super-admin-only capability; the API enforces the same
  // rule, this just keeps the button out of everyone else's toolbar.
  const canExport = useIsSuperAdmin();

  // The header checkbox speaks for THIS PAGE only, while the footer count is
  // the whole cross-page selection — the two numbers differ on purpose, and
  // conflating them would make "select all" look like it cleared a selection
  // the operator built up elsewhere.
  const selectedCount = selectedIds.size;
  const pageIds = tasks.map((task) => task.id);
  const pageSelectedCount = pageIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;

  const hasActiveSortFilter =
    filter.annotationFilter !== "all" ||
    filter.reviewFilter !== "all" ||
    filter.labelIds.length > 0 ||
    filter.updatedBy.length > 0 ||
    sort.sortBy !== DEFAULT_SORT.sortBy ||
    sort.sortOrder !== DEFAULT_SORT.sortOrder;

  useEffect(() => {
    if (selectedTaskId === null) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-task-id="${selectedTaskId}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedTaskId, tasks]);

  const handleExport = async () => {
    if (!activeProject) return;
    try {
      const data = await triggerExport({
        projectId: activeProject.id,
        annotated:
          filter.annotationFilter === "all"
            ? undefined
            : filter.annotationFilter,
        labelIds: filter.labelIds.length
          ? filter.labelIds.join(",")
          : undefined,
      }).unwrap();

      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const slug = activeProject.name
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate(),
      )}-${pad(now.getHours())}${pad(now.getMinutes())}`;

      const a = document.createElement("a");
      a.href = url;
      a.download = `tasks-${slug}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to export tasks", e);
      toast.error(t("dataSource.exportFailed"));
    }
  };

  return (
    <div className="flex max-h-full min-h-0 flex-col overflow-hidden border-r border-black/10 bg-black/[0.03]">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-black/10 px-4">
        <Checkbox
          checked={allOnPageSelected}
          indeterminate={pageSelectedCount > 0 && !allOnPageSelected}
          onCheckedChange={onToggleSelectPage}
          disabled={batchRunning || pageIds.length === 0}
          aria-label={
            allOnPageSelected ? t("dataSource.deselectPage") : t("dataSource.selectPage")
          }
          title={
            allOnPageSelected ? t("dataSource.deselectPage") : t("dataSource.selectPage")
          }
        />
        <p className="flex-1 text-[10px] font-bold uppercase tracking-[1px] text-foreground/90">
          {t("dataSource.title")}
        </p>
        <button
          type="button"
          aria-label={t("dataSource.sortFilterAria")}
          title={t("sortFilter.title")}
          onClick={() => setSortFilterDialogOpen(true)}
          className={cn(
            "flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground transition-colors hover:bg-black/[0.06] hover:text-foreground",
            hasActiveSortFilter && "bg-primary/10 text-primary",
          )}
        >
          <ArrowDownWideNarrow className="size-4" />
        </button>
        <button
          type="button"
          aria-label={t("dataSource.viewAnalytics")}
          title={t("dataSource.viewAnalytics")}
          onClick={() => setAnalyticsDialogOpen(true)}
          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground transition-colors hover:bg-black/[0.06] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChartSpline className="size-4" />
        </button>
        {canExport && (
          <button
            type="button"
            aria-label={t("dataSource.exportAria")}
            title={t("dataSource.exportTitle")}
            onClick={handleExport}
            disabled={isExporting || !activeProject}
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground transition-colors hover:bg-black/[0.06] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="size-4" />
          </button>
        )}
      </div>

      <TaskSortFilterDialog
        open={sortFilterDialogOpen}
        onOpenChange={setSortFilterDialogOpen}
        sort={sort}
        filter={filter}
        labels={labels}
        metricsAvailable={metricsAvailable}
        onApply={onSortFilterChange}
      />
      <AnalyticsDialog
        open={analyticsDialogOpen}
        onOpenChange={setAnalyticsDialogOpen}
      />

      <div ref={listRef} className="flex flex-1 flex-col overflow-y-auto">
        {tasks.map((task) => {
          const isSelected = task.id === selectedTaskId;
          const count = isSelected
            ? annotationCount
            : (task.annotationCount ?? 0);
          // The count chip is suppressed while awaiting review unless the task
          // carries real SAVED work — `task.annotationCount`, not `count`.
          // `count` is the live editor total on the selected row, which
          // includes the staged predictions themselves, so using it would
          // render "Review · 3" for an image with nothing saved at all.
          const showCount = !task.awaitingReview || (task.annotationCount ?? 0) > 0;
          const batchStatus = batchItemStatus.get(task.id);
          return (
            <div key={task.id} data-task-id={task.id} className="relative">
              {/*
                A sibling of the row, not a child: TaskListItem renders as a
                <button> whenever it is clickable, and a checkbox inside a
                button is invalid HTML whose clicks the row would swallow.
                `leadingInset` reserves the space this sits in.
              */}
              <Checkbox
                checked={selectedIds.has(task.id)}
                onClick={(event) =>
                  onToggleSelect(task.id, (event as MouseEvent).shiftKey)
                }
                disabled={batchRunning}
                aria-label={t("dataSource.selectTask", { iid: task.iid })}
                className="absolute left-3 top-1/2 z-10 -translate-y-1/2"
              />
              <TaskListItem
                task={task}
                leadingInset
                imageSrc={task.thumbnailUrl}
                selected={isSelected}
                onClick={() => onSelectTask(task.id)}
                titleSlot={
                  task.running || runningTaskIds.has(task.id) ? (
                    <RunningChip />
                  ) : batchStatus ? (
                    <BatchChip status={batchStatus} />
                  ) : task.awaitingReview ? (
                    <ReviewChip />
                  ) : undefined
                }
                rightSlot={
                  showCount ? (
                    <AnnotationChip count={count} status={task.status} />
                  ) : undefined
                }
                metrics={
                  showTaskMetrics
                    ? {
                        meanConfidence: task.meanConfidence,
                        meanIou: task.meanIou,
                        precision: task.precision,
                        recall: task.recall,
                      }
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-black/10 px-3 py-2.5">
        <button
          type="button"
          onClick={onClearSelection}
          disabled={selectedCount === 0}
          className={cn(
            "min-w-0 flex-1 truncate text-left text-xs",
            selectedCount > 0
              ? "cursor-pointer text-foreground/90 hover:underline"
              : "cursor-default text-foreground/45",
          )}
          title={selectedCount > 0 ? t("dataSource.clearSelection") : undefined}
        >
          {selectedCount > 0
            ? t("dataSource.selected", { count: selectedCount })
            : t("common:empty.noneSelected")}
        </button>
        <Button
          size="sm"
          className="h-8 shrink-0"
          onClick={onAutoAnnotate}
          disabled={selectedCount === 0 || batchRunning}
          title={
            batchRunning
              ? t("dataSource.batchRunning")
              : selectedCount === 0
                ? t("dataSource.selectToAutoAnnotate")
                : t("dataSource.autoAnnotateSelected", { count: selectedCount })
          }
        >
          <Sparkles className="size-4" />
          {t("actions.autoAnnotate")}
          {selectedCount > 0 && (
            <span className="ml-0.5 rounded-full bg-white/25 px-1.5 font-mono text-[11px] leading-[18px]">
              {selectedCount}
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="size-8 shrink-0"
          onClick={onOpenAutoAnnotateSettings}
          aria-label={t("actions.autoAnnotationSettings")}
          title={t("actions.autoAnnotationSettings")}
        >
          <Settings2 className="size-4" />
        </Button>
      </div>

      <div className="flex shrink-0 items-center gap-4 border-t border-black/10 px-2 py-4">
        <PaginationNumbers
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
};

/**
 * Where an image has got to inside the running batch.
 *
 * Occupies the same slot as the Review chip and takes precedence over it while
 * a batch is live: one row, one meaning at a time. A DONE item becomes a
 * Review chip on its own as soon as the batch settles and the list refetches —
 * which is why this deliberately has no "done" wording of its own to contradict
 * it with.
 */
const BatchChip = ({ status }: { status: AutoAnnotationBatchItemStatusEnum }) => {
  const { t } = useTranslation(["label", "common"]);
  if (status === AutoAnnotationBatchItemStatusEnum.PENDING) return <RunningChip />;
  if (status === AutoAnnotationBatchItemStatusEnum.FAILED) {
    return (
      <span
        title={t("dataSource.chip.failedTitle")}
        className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-semibold leading-4 text-amber-700"
      >
        <CircleAlert className="size-2.5" />
        {t("dataSource.chip.failed")}
      </span>
    );
  }
  return <ReviewChip />;
};

/** "Running" chip: an auto-annotation turn is in flight, from a batch or a single run. */
const RunningChip = () => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <span
      title={t("dataSource.chip.runningTitle")}
      className="flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-1.5 py-px text-[10px] font-semibold leading-4 text-violet-700"
    >
      <Loader2 className="size-2.5 animate-spin" />
      {t("dataSource.chip.running")}
    </span>
  );
};

/**
 * "Review" chip: this image holds auto-annotations nobody has looked at yet,
 * or was auto-annotated and came back empty.
 *
 * Violet rather than the emerald used for saved work — it is a call to action,
 * not a completion state, and it must not read as "done" at a glance while
 * scanning the sidebar.
 */
const ReviewChip = () => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <span
      title={t("dataSource.chip.reviewTitle")}
      className="flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-1.5 py-px text-[10px] font-semibold leading-4 text-violet-700"
    >
      <Sparkles className="size-2.5" />
      {t("dataSource.chip.review")}
    </span>
  );
};

/**
 * Small "annotation chip" badge: a hairline-bordered rectangle with the
 * annotation count inside and two diagonal corner dots — matches figma's
 * crop-mark styling on the data source rows.
 */
const AnnotationChip = ({
  count,
  status,
}: {
  count: number;
  status: string;
}) => (
  <div className="relative flex h-4 shrink-0 items-center justify-center rounded-[2px] border border-gray-300 bg-black/[0.03] px-1 text-[11px] leading-3 text-foreground/60">
    {count === 0 && status === TaskStatusEnum.DONE ? (
      <Check className="size-3 text-emerald-500" />
    ) : (
      count
    )}
    <span className="absolute -left-[2.5px] -top-[2.5px] size-1 rounded-full bg-gray-300" />
    <span className="absolute -bottom-[2.5px] -right-[2.5px] size-1 rounded-full bg-gray-300" />
  </div>
);
